/**
 * Boîtiers GPS (migration 0026) — logique PURE : points à dessiner, étiquettes,
 * bloc de configuration Traccar, messages d'erreur. Aucun accès réseau ici (c'est
 * ce qui se teste, `gps.test.ts`) ; les appels sont dans `boitiers.ts`.
 *
 * Traccar tourne en dessous et POUSSE ses positions vers l'edge function
 * `traccar-webhook` : la console n'appelle jamais Traccar.
 */
import { coordonneeValide, etatPosition } from './geo'
import type { Boitier, Livreur, VehiculeOps } from './types'

/**
 * Le nom sous lequel un boîtier s'affiche PARTOUT (carte, listes, alertes) : le nom donné au boîtier (le même qu'à Traccar,
 * 0052), sinon la plaque du véhicule rattaché, sinon son identifiant.
 */
export function nomBoitier(b: Pick<Boitier, 'label' | 'vehicle_id' | 'unique_id'>, vehicules: readonly Pick<VehiculeOps, 'id' | 'plate'>[]): string {
  const nom = b.label?.trim()
  if (nom) return nom
  const plaque = b.vehicle_id ? vehicules.find((v) => v.id === b.vehicle_id)?.plate?.trim() : ''
  return plaque || b.unique_id
}

/** Point d'un véhicule à boîtier sur la carte (les boîtiers rattachés à un livreur passent par SA fiche). */
export interface PointBoitier {
  id: string
  /** Nom du boîtier (voir `nomBoitier`) : celui que l'on retrouve dans Traccar et dans les alertes. */
  libelle: string
  /** Plaque et modèle du véhicule (sauf si la plaque est déjà le nom), ou `null`. */
  detail: string | null
  lat: number
  lng: number
  ageMs: number
  recente: boolean
  vitesseKmh: number | null
  cap: number | null
}

/**
 * Les boîtiers à dessiner comme véhicules : actifs, avec une position exploitable,
 * et NON rattachés à un livreur connu — celui-ci est déjà sur la carte avec la
 * position du boîtier (la fonction de réception l'écrit dans `drivers.lat/lng`),
 * un second marqueur au même endroit doublerait le point.
 */
export function pointsBoitiers(
  boitiers: readonly Boitier[],
  vehicules: readonly VehiculeOps[],
  livreurs: readonly Pick<Livreur, 'id'>[],
  maintenant: number = Date.now(),
): PointBoitier[] {
  const points: PointBoitier[] = []
  for (const b of boitiers) {
    if (!b.active) continue
    if (b.driver_id && livreurs.some((l) => l.id === b.driver_id)) continue
    if (!coordonneeValide(b.last_lat, b.last_lng)) continue
    const pos = etatPosition({ lat: b.last_lat, lng: b.last_lng, last_seen: b.last_fix_at }, maintenant)
    if (pos.etat === 'aucune') continue
    const v = b.vehicle_id ? vehicules.find((x) => x.id === b.vehicle_id) : undefined
    points.push({
      id: b.id,
      libelle: nomBoitier(b, vehicules),
      detail: [v?.plate, v?.model].filter((x) => x && x !== nomBoitier(b, vehicules)).join(' · ') || null,
      lat: b.last_lat as number,
      lng: b.last_lng as number,
      ageMs: pos.ageMs,
      recente: pos.etat === 'recente',
      vitesseKmh: b.last_speed_kmh,
      cap: b.last_course,
    })
  }
  return points
}

/** « Moto A-1234 », « Moussa », « Moto A-1234 · Moussa », « Non rattaché ». */
export function libelleRattachement(
  b: Pick<Boitier, 'vehicle_id' | 'driver_id'>,
  vehicules: readonly VehiculeOps[],
  livreurs: readonly Pick<Livreur, 'id' | 'full_name'>[],
): string {
  const v = b.vehicle_id ? vehicules.find((x) => x.id === b.vehicle_id) : undefined
  const l = b.driver_id ? livreurs.find((x) => x.id === b.driver_id) : undefined
  const morceaux = [v ? v.plate || v.model || 'Véhicule' : null, l ? l.full_name : null].filter(Boolean)
  return morceaux.length > 0 ? morceaux.join(' · ') : 'Non rattaché'
}

/** Source affichée d'un livreur : « boîtier GPS » ou « app livreur ». */
export function libelleSource(source: 'app' | 'boitier' | undefined): string {
  return source === 'boitier' ? 'boîtier GPS' : 'app livreur'
}

export const SECRET_PREFIXE = 'sdgps_'

/** URL de l'edge function, à coller dans Traccar. */
export function urlWebhook(urlSupabase: string): string {
  return `${urlSupabase.replace(/\/+$/, '')}/functions/v1/gps-reception`
}

/**
 * Les trois lignes à ajouter dans `traccar.xml` (entre `<properties>` et
 * `</properties>`). Clés vérifiées dans la documentation et le code de Traccar :
 * `forward.url`, `forward.type` (json), `forward.header` (« Nom: valeur »).
 */
export function blocTraccar(secret: string, urlSupabase: string): string {
  return [
    `<entry key="forward.url">${urlWebhook(urlSupabase)}</entry>`,
    `<entry key="forward.type">json</entry>`,
    `<entry key="forward.header">X-Swift-Secret: ${secret}</entry>`,
  ].join('\n')
}

/** Le secret, à moitié masqué, pour l'affichage d'un état — jamais le secret entier. */
export function secretMasque(): string {
  return `${SECRET_PREFIXE}${'•'.repeat(12)}`
}

/** Erreur de la base ou d'une RPC → phrase lisible. */
export function messageErreurBoitier(e: { message?: string; details?: string | null } | null | undefined): string {
  const texte = `${e?.message ?? ''} ${e?.details ?? ''}`
  if (/plan_required|row-level security/i.test(texte)) {
    return "Le suivi par boîtier GPS n'est pas disponible pour cette organisation, ou votre rôle ne permet pas de gérer les boîtiers."
  }
  if (/duplicate key|unique/i.test(texte)) return 'Ce boîtier est déjà enregistré (même identifiant).'
  if (/gps_vehicle_other_org/.test(texte)) return "Ce véhicule n'appartient pas à votre organisation."
  if (/gps_driver_other_org/.test(texte)) return "Ce livreur n'appartient pas à votre organisation."
  if (/forbidden|permission denied/i.test(texte)) return "Votre rôle ne permet pas de gérer les boîtiers."
  return e?.message || 'Opération impossible.'
}

/** Identifiant saisi : espaces retirés, vide ou trop long refusé. */
export function identifiantValide(brut: string): string | null {
  const v = brut.trim()
  return v.length >= 1 && v.length <= 64 ? v : null
}
